import {
  OrgTransactionResponseDTODataItem,
  TransactionResponseDTODataItem,
} from "@/generated/api/models";
import { useCallback, useMemo } from "react";
import { getI18n } from "react-i18next";
import { useSumFormatter } from "./useSumFormatter";
import { centsToValue } from "./currency.precision";
import {
  CheckCircle,
  Euro,
  DollarSign,
  LucideIcon,
  MessageCircle,
  Send,
} from "lucide-react-native";

export type UseTransactionInfoProps = Pick<
  TransactionResponseDTODataItem,
  "currency"
>;

export function useTransactionInfo(data: UseTransactionInfoProps) {
  const { formatCents, sumFormatter, currencyCode } = useSumFormatter({
    asset: data.currency,
  });

  const dateFormatter = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  }, []);

  const currencyIcon = currencyCode === "EUR" ? Euro : DollarSign;

  const getOrgTransactionDetails = useCallback(
    ({
      amount,
      metadata,
      items,
    }: Pick<
      OrgTransactionResponseDTODataItem,
      "amount" | "metadata" | "items"
    >) => {
      let icon: LucideIcon = currencyIcon;
      let name = "Transaction";

      if (amount > 0) {
        name = metadata?.isTip ? "Incoming tips" : "Incoming funds";
      } else if (amount < 0) {
        name = "Withdrawal";
      } else if (metadata?.isFeedback) {
        name = "Feedback";
        icon = MessageCircle;
      } else if (items?.[0]?.transactions?.[0]?.category === "holdtomain") {
        name = "Processing complete";
      } else {
        name = "Transfer";
        amount = items.reduce(
          (acc, v1) =>
            acc +
            v1.transactions.reduce(
              (acc, v2) => acc + Math.min(v2.amount, 0),
              0
            ),
          0
        );
      }

      return {
        icon,
        name,
        amount,
        category: undefined,
      };
    },
    [currencyIcon]
  );

  const getTransactionDetails = useCallback(
    (
      additionalData: Pick<
        TransactionResponseDTODataItem,
        | "amount"
        | "category"
        | "holdAmount"
        | "metadata"
        | "holdOverdraft"
        | "overdraft"
      >
    ) => {
      let icon: LucideIcon = currencyIcon;
      let name: string = additionalData.category;
      let amount: number = additionalData.amount;
      let holdAmount: number = additionalData.holdAmount;

      // if (additionalData.holdOverdraft > 0) {
      //   holdAmount -= additionalData.holdOverdraft;
      // }

      // if (additionalData.overdraft > 0) {
      //   amount -= additionalData.overdraft;
      // }

      let totalAmount = amount;

      switch (additionalData.category) {
        case "other":
          icon = currencyIcon;
          name = `Operation. Balance change: ${formatCents(
            amount
          )}; Hold change: ${formatCents(holdAmount)}`;
          totalAmount = amount + holdAmount;
          break;
        case "holdtomain":
          icon = CheckCircle;
          name = `${formatCents(amount)} is available for payout`;
          totalAmount = 0;
          break;
        case "holdtopup":
          icon = currencyIcon;
          name = additionalData.metadata?.isTip
            ? holdAmount === 0
              ? `Incoming tips`
              : `Incoming tips`
            : `Transfer`;
          totalAmount = holdAmount;
          break;
        case "maintopup":
          icon = currencyIcon;
          name = additionalData.metadata?.isTip
            ? `Incoming tips`
            : `Shared tips`;
          totalAmount = amount;
          break;
        case "payout":
          icon = Send;
          name = `Payout`;
          totalAmount = amount + holdAmount;
          break;
        case "feedback":
          icon = MessageCircle;
          name = `Feedback without tips`;
          totalAmount = amount;
          break;
      }

      return {
        icon,
        name,
        amount: totalAmount,
        category: additionalData.category
      };
    },
    [currencyIcon]
  );

  return {
    formatCents,
    getDetails: (
      transaction:
        | Parameters<typeof getTransactionDetails>[0]
        | Parameters<typeof getOrgTransactionDetails>[0]
    ) => {
      if ("items" in transaction) {
        if (
          transaction.items.length === 1 &&
          transaction.items[0].transactions.length === 1
        ) {
          return getTransactionDetails(transaction.items[0].transactions[0]);
        }

        return getOrgTransactionDetails(transaction);
      }

      return getTransactionDetails(transaction);
    },
    dateFormatter,
    sumFormatter,
  };
}

export type UseTransactionInfoResult = ReturnType<typeof useTransactionInfo>;
